import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import Oops from '~/common/components/Oops';
import { SIGNAL_R_STATUS } from '~/common/constants';
import { useUser } from '~/common/hooks/useAuthentication';
import useBoltStatus from '~/common/hooks/useBoltStatus';
import useUrl from '~/common/hooks/useUrl';
import { typedApiClient } from '~/common/util/api-client';
import AltMessageScreen from '~/pages/ClassifyDocumentsV2/AltMessageScreen';
import ClassifyRightContainer from '~/pages/ClassifyDocumentsV2/ClassifyRightContainer';
import QuickMatch from '~/pages/ClassifyDocumentsV2/QuickMatch';
import { ClassifyRuleIds } from '~/pages/ClassifyDocumentsV2/constants';
import { useRefreshNavigationDetailsMutation } from '~/pages/ClassifyDocumentsV2/hooks/mutations';
import { HomeActions } from '~/pages/Home/redux';
import { useNavigation } from '~/stores/navigationStore';
import { useSignalRRealtime } from '~/stores/useSignalRStore';

import { StyledClassifyGrid } from './StyledComponents/ClassifyRightContainer.styles';
import { LOADER_MESSAGE } from './language.json';

export default function ClassifyDocumentsV2() {
  const obfuscatedLoanIdentifier = useUrl().obfuscatedLoanIdentifier;
  const { selectedAlert, selectedCategory } = useNavigation();
  const { isUnderwriter, hasAccessToUnderwriterView } = useUser();
  const dispatch = useDispatch();
  const {
    mutate: refetchLeftGutter,
    isError: refetchError,
    isLoading: isRefetching,
  } = useRefreshNavigationDetailsMutation();
  const [documentProcess, setDocumentProcess] = useState<
    'adr' | 'ade' | 'complete'
  >(null);
  const [isAdrTimeout, setIsAdrTimeout] = useState(null);

  const refetchAndComplete = useCallback(() => {
    refetchLeftGutter(undefined, {
      onSettled: () => setDocumentProcess('complete'),
    });
  }, [refetchLeftGutter]);

  const isLoadingNavigation = isRefetching || !selectedCategory;
  const isLoanInUnderwritingError =
    useBoltStatus().data?.isLoanSubmitted &&
    !(isUnderwriter || hasAccessToUnderwriterView);
  const isClassifyAvailable =
    !isLoadingNavigation && !isLoanInUnderwritingError;
  const isLoadingAdr = isClassifyAvailable && documentProcess === 'adr';
  const isAwaitingAde = isClassifyAvailable && documentProcess === 'ade';
  const isOopsError = refetchError || isAdrTimeout;
  const isLoading = !!documentProcess || isLoadingNavigation;

  const isNoDocumentsError = useMemo(() => {
    const docTabs =
      selectedCategory?.navigationGutterItems.filter(
        (x) =>
          x.ruleId === ClassifyRuleIds.DocumentAction ||
          x.ruleId === ClassifyRuleIds.Completed,
      ) ?? [];

    if (!selectedCategory) {
      return null;
    }

    return (
      docTabs.length > 0 &&
      docTabs.every((x) => x.gutterItemChildren.length === 0)
    );
  }, [selectedCategory]);

  useSignalRRealtime({
    notifications: SIGNAL_R_STATUS.ADR_PROCESS_COMPLETED,
    onMessage() {
      if (documentProcess === 'adr') {
        setDocumentProcess('ade');
      } else {
        refetchAndComplete();
      }
    },
  });

  useSignalRRealtime({
    notifications: SIGNAL_R_STATUS.CLASSIFY_STATUS_CHANGE,
    onMessage() {
      let isAdrPending: boolean;
      typedApiClient.v1
        .classifyDocumentsAdrPendingList(
          obfuscatedLoanIdentifier.obfuscatedLenderDatabaseId,
          obfuscatedLoanIdentifier.obfuscatedLoanRecordId,
        )
        .then(({ data }) => (isAdrPending = data))
        .catch(() => (isAdrPending = false))
        .then(() => {
          if (!isAdrPending) {
            return;
          }

          setDocumentProcess('adr');
        });
    },
  });

  // Initialize the page.
  useEffect(() => {
    if (
      selectedCategory &&
      !isLoanInUnderwritingError &&
      !selectedCategory.navigationGutterItems.length
    ) {
      setDocumentProcess('adr');
      return;
    }

    if (
      selectedAlert?.ruleId ||
      !selectedCategory?.navigationGutterItems?.length
    ) {
      return;
    }

    const firstAlert = selectedCategory.navigationGutterItems.flatMap((x) =>
      x.gutterItemChildren.length ? x.gutterItemChildren : [x],
    )[0];
    dispatch(HomeActions.setSelectedAlert(firstAlert));
  }, [selectedAlert, selectedCategory, dispatch, isLoanInUnderwritingError]);

  useEffect(() => {
    if (isLoadingAdr) {
      // show error in 2 minutes unless ADR resolves.
      const timeout = setTimeout(
        () => setIsAdrTimeout((previous: boolean | null) => previous ?? true),
        2 * 60_000,
      );
      return () => clearTimeout(timeout);
    }
  }, [isLoadingAdr]);

  useEffect(() => {
    if (!isAwaitingAde) {
      return;
    }

    // Show the user a 30-second delay to wait for extraction to complete.
    const timeout = setTimeout(() => {
      setDocumentProcess(null);
      refetchAndComplete();
    }, 30 * 1_000);

    return () => clearTimeout(timeout);
  }, [isAwaitingAde, refetchAndComplete]);

  useEffect(() => {
    if (documentProcess !== 'complete') {
      return;
    }

    // Allow the completion animation (500ms) to finish before clearing the loading screen.
    const timeout = setTimeout(() => setDocumentProcess(null), 1_000);
    return () => clearTimeout(timeout);
  }, [documentProcess]);

  if (isLoanInUnderwritingError) {
    return <AltMessageScreen message={LOADER_MESSAGE.UNDERWRITING} />;
  }

  if (selectedAlert?.ruleId === ClassifyRuleIds.Error) {
    return <AltMessageScreen message={LOADER_MESSAGE.NO_NAVIGATIONS_ITEMS} />;
  }

  if (isOopsError) {
    return <Oops />;
  }

  if (isNoDocumentsError) {
    return <AltMessageScreen message={LOADER_MESSAGE.NO_NEW_DOCS} showFooter />;
  }

  if (isLoading || !selectedAlert) {
    const status = (() => {
      switch (true) {
        case isRefetching:
          return 'refetch-navigation';
        case isLoadingNavigation:
          return 'navigation';
        default:
          return documentProcess;
      }
    })();

    return <AltMessageScreen loading={status} />;
  }

  return (
    <StyledClassifyGrid>
      {selectedAlert.ruleId === ClassifyRuleIds.QuickMatch && <QuickMatch />}
      {selectedAlert.ruleId === ClassifyRuleIds.DocumentView && (
        <ClassifyRightContainer />
      )}
    </StyledClassifyGrid>
  );
}
